import { AlertPriority, AlertSeverity } from "@/client";
import { OptionType } from "@/components/select-input/select-input";
import { Row, SortingFn } from "@tanstack/react-table";

// Read off the generated enums rather than listed here, so the order the pickers and filters show is
// the backend's own enum order (least to most severe) - the same order the alert lists sort by.
export const ALERT_PRIORITY_VALUES = Object.values(AlertPriority);
export const ALERT_SEVERITY_VALUES = Object.values(AlertSeverity);

// Annotated rather than inferred: without it the option value widens to a plain string union and the
// enum each mutation expects no longer matches.
export const ALERT_PRIORITY_OPTIONS: OptionType<AlertPriority>[] =
  ALERT_PRIORITY_VALUES.map((value) => ({ label: value, value }));
export const ALERT_SEVERITY_OPTIONS: OptionType<AlertSeverity>[] =
  ALERT_SEVERITY_VALUES.map((value) => ({ label: value, value }));

export const asAlertSettingOption = <TValue extends string>(
  value: TValue,
): OptionType<TValue> => ({ label: value, value });

// The tables sort server-side, but TanStack still re-sorts each returned page and its default
// comparator is alphanumeric -- which for these enums reads Blocker, Minor, Normal, Urgent and
// contradicts the order the backend just applied. These comparators rank by enum position so the two
// agree. Absent values rank last, matching Postgres, which puts NULLs last ascending and first
// descending.
const enumRankSortingFn =
  <TData>(order: readonly string[]): SortingFn<TData> =>
  (rowA, rowB, columnId) => {
    const rank = (row: Row<TData>) => {
      const value = row.getValue<string | null | undefined>(columnId);
      const index = value == null ? -1 : order.indexOf(value);
      return index === -1 ? order.length : index;
    };

    return rank(rowA) - rank(rowB);
  };

export const alertPrioritySortingFn = <TData>(): SortingFn<TData> =>
  enumRankSortingFn<TData>(ALERT_PRIORITY_VALUES);
export const alertSeveritySortingFn = <TData>(): SortingFn<TData> =>
  enumRankSortingFn<TData>(ALERT_SEVERITY_VALUES);
